import ReceiptImage from '../assets/img/icons/transactions/receipt-text.png';
import CheckImage from '../assets/img/icons/transactions/check.png';
import NotebookImage from '../assets/img/icons/transactions/notebook.png';
import PlusImage from '../assets/img/icons/transactions/plus.png';
import WarningImage from '../assets/img/icons/transactions/triangle-alert.png';
import UserCheckImage from '../assets/img/icons/transactions/user-round-check.png';
import LoaderImage from '../assets/img/icons/transactions/loader.png';

import React, { useCallback, useState, useEffect } from 'react';
import { useIntl } from '../_library/intl';
import { Text, Form } from './';
import { api } from '../_library';
import * as Yup from 'yup';

export type ReceiptStatus = 'add' | 'processing' | 'valid' | 'not_valid' | 'overdue';

export type Transaction = {
    id: string | number;
    receiptAllowed: boolean;
    receiptStatus: ReceiptStatus;
};

export const receiptStatusMap = {
        add: {
            icons: [PlusImage],
            text: "bk.transaction_receipt.add",
        },
        processing: {
            icons: [LoaderImage, ReceiptImage],
            text: "bk.transaction_receipt.processing",
        },
        valid: {
            icons: [CheckImage, ReceiptImage],
            text: "bk.transaction_receipt.valid",
        },
        not_valid: {
            icons: [WarningImage, ReceiptImage],
            text: "bk.transaction_receipt.not_valid",
        },
        approved_no_receipt: {
            icons: [UserCheckImage, ReceiptImage],
            text: "bk.transaction_receipt.approved_no_receipt",
        },
        approved_invalid: {
            icons:[NotebookImage, ReceiptImage],
            text: "bk.transaction_receipt.approved_invalid",
        },
        overdue: {
            icons: [WarningImage, ReceiptImage],
            text: "bk.transaction_receipt.overdue",
        }
} as const;

export const getReceiptStatus = (status: ReceiptStatus, allowed: boolean) => {
    if (!allowed) return null;
    return receiptStatusMap[status] ?? receiptStatusMap.add;
}

export const canUploadForStatus = (status: ReceiptStatus, allowed: boolean) =>
    allowed && ['add','not_valid'].includes(status);

export const canApproveForStatus = (status: ReceiptStatus, allowed: boolean) =>
    allowed && ['add','processing','not_valid'].includes(status);

export const canUnapproveForStatus = (_status: ReceiptStatus) => false;

type Props = {
    transaction: Transaction;
    onUploadComplete?: () => void;
};

export const TransactionReceipt: (props: Props) => JSX.Element | null = ({ transaction, onUploadComplete }) => {
    const intl = useIntl();
    const [file, setFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string>('');
    const [isSuccess, setIsSuccess] = useState(false);

    useEffect(() => {
        setFile(null);
        setError('');
        setIsSuccess(false);
        setIsUploading(false);
    }, [transaction.id]);

    const canUpload = canUploadForStatus(transaction.receiptStatus, transaction.receiptAllowed);

    const ACCEPT_MIME = 'image/png,image/jpg,image/jpeg,image/gif,image/bmp,image/webp';

    const initFileValues = { file: null as File | null };

    const validationFileSchema = {
        file: Yup.string()
            .required(intl.formatMessage({id: 'bk.transaction_receipt.error.no_file'})),
    };

    const inputFileFields = [
    {
        name: 'file',
        type: 'file',
        label: intl.formatMessage({ id: 'bk.transaction_receipt.file_label' }),
        accept: ACCEPT_MIME,
    },
    ];

    const handleFileSubmit = useCallback(async (values: any) => {
        if (isUploading) return;

        // FormFileInput stores the picked file as a base64 data URL
        // (FileReader.readAsDataURL), never as a File object, so rebuild a Blob
        // here for the multipart upload the backend expects.
        const rawValue = values?.file;

        if (!rawValue || typeof rawValue !== 'string' || rawValue.indexOf(',') === -1) {
            setError(intl.formatMessage({ id: 'bk.transaction_receipt.error.no_file' }));
            return;
        }

        let selected: File;

        try {
            const [dataUrlMeta, base64Data] = rawValue.split(',');
            const mimeType = (/:(.*?);/.exec(dataUrlMeta) || [])[1] || 'image/jpeg';
            const binary = atob(base64Data);
            const bytes = new Uint8Array(binary.length);

            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
            }

            // the backend validates the extension of the sent filename, so it
            // has to be one of the allowed image extensions
            const extension = (mimeType.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
            selected = new File([bytes], `receipt.${extension}`, { type: mimeType });
        } catch (e) {
            setError(intl.formatMessage({ id: 'bk.transaction_receipt.error.no_file' }));
            return;
        }

        setIsUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', selected);
            await api.post(`/transactions/${transaction.id}/uploadReceipt`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            });
            setIsSuccess(true);
            setError('');
            onUploadComplete?.();
        } catch {
            setError(intl.formatMessage({ id: 'bk.transaction_receipt.error.upload_failed' }));
        } finally {
            setIsUploading(false);
        }
    }, [isUploading, transaction.id, intl, onUploadComplete]);

    const ui = getReceiptStatus(transaction.receiptStatus, transaction.receiptAllowed);
    if (!ui) return null;

    return (
        <div className="space-y-3">
            <div className="flex space-x-1">
                {ui.icons.map((src: string, i: number) => (
                    <img key={i} alt="icon" src={src} className="w-5 h-5" />
                ))}
            </div>
            <Text label={ui.text} />

            {canUpload && !isSuccess ? (
                <Form
                    className="mt-4"
                    enableReinitialize
                    initValues={initFileValues}
                    validationSchema={validationFileSchema}
                    onSubmit={handleFileSubmit}
                    fields={inputFileFields}
                    submitBtnLabel="bk.transaction_receipt.btn.upload"
                    submitBtnFullWidth
                />
            ) : null}
            {error ? <Text className="text-bk-red" label={error} /> : null}
            {isSuccess ? <Text className="text-bk-green" label="bk.transaction_receipt.success" /> : null}
        </div>
    );
};